/**
 * Certificate of Completion — layout and download.
 *
 * Rendered entirely in the browser from the enrollment record the portal
 * already holds, so it works for any completed course regardless of when it was
 * completed: nothing here depends on a server-side render or on fields that
 * only newer completions carry. Fields the record doesn't have (a certificate
 * number that predates server-issued numbering, for instance) are omitted and
 * the remaining ones re-centred, rather than faked.
 *
 * See ./pdfWriter for the (deliberately small) PDF primitives, and its header
 * for the one real limitation: the base-14 fonts cover Latin-1 only, so course
 * titles in non-Latin scripts will not render until a font is embedded.
 */

import { measureText } from './fontMetrics';
import { LION_MARK, LION_WORDMARK } from './lionMarks';
import {
  PAGE_HEIGHT,
  PAGE_WIDTH,
  PdfPage,
  renderPdf,
  rgb,
  toLatin1,
} from './pdfWriter';

/** Print palette — fixed light values; a certificate is not theme-aware. */
const INK = rgb('#111928'); // gray-900
const INK_SOFT = rgb('#374151'); // gray-700
const MUTED = rgb('#6B7280'); // gray-500
const RULE = rgb('#D1D5DB'); // gray-300
const GOLD = rgb('#FFCF51'); // Lion Energy yellow-500
const PAPER = rgb('#FFFFFF');

const CENTER_X = PAGE_WIDTH / 2;
const OUTER_MARGIN = 30;
const INNER_MARGIN = 38;
/** Widest a headline may be before it is shrunk to fit. */
const CONTENT_WIDTH = 620;

/**
 * Vertical band the citation block is centred in: from the footer divider up to
 * the gold rule under the heading. Both of those are fixed, so the block floats
 * between them regardless of how tall it turns out.
 */
const BAND_TOP = 416;
const BAND_BOTTOM = 196;

export type CertificateData = {
  /** Full name of the person the certificate is issued to. */
  recipientName: string;
  /** Course / module title, as shown in the portal. */
  courseTitle: string;
  /** ISO timestamp of completion; omitted from the certificate when null. */
  completedAt?: string | null;
  /** Server-issued number; omitted when the enrollment predates numbering. */
  certificateNumber?: string | null;
  /** Localised strings — see {@link DEFAULT_CERTIFICATE_LABELS}. */
  labels?: Partial<CertificateLabels>;
  /** BCP 47 tag used to format the completion date. Defaults to the browser's. */
  locale?: string;
};

export type CertificateLabels = {
  heading: string;
  presentedTo: string;
  completionLine: string;
  completedOn: string;
  certificateNumber: string;
  issuedBy: string;
};

export const DEFAULT_CERTIFICATE_LABELS: CertificateLabels = {
  heading: 'Certificate of Completion',
  presentedTo: 'This certifies that',
  completionLine: 'has successfully completed',
  completedOn: 'Completed on',
  certificateNumber: 'Certificate No.',
  issuedBy: 'Issued by Lion Energy',
};

function formatCompletionDate(iso: string, locale?: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Largest size at or below `maxSize` at which `text` fits `maxWidth`, stepping
 * down in whole points. Long names and course titles are the norm, not the
 * exception, so the headline scales instead of overflowing the border.
 */
function fitSize(
  text: string,
  font: 'Helvetica' | 'Helvetica-Bold',
  maxSize: number,
  minSize: number,
  maxWidth: number,
  charSpacing = 0,
): number {
  let size = maxSize;
  while (size > minSize && measureText(text, font, size, charSpacing) > maxWidth) {
    size -= 1;
  }
  return size;
}

/** Greedy word wrap to at most `maxLines`; the last line is ellipsised if needed. */
function wrapLines(
  text: string,
  font: 'Helvetica' | 'Helvetica-Bold',
  size: number,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (measureText(candidate, font, size, 0) <= maxWidth || !current) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = word;
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && current) lines.push(current);

  if (lines.length === maxLines) {
    const consumed = lines.join(' ').split(/\s+/).length;
    if (consumed < words.length) {
      let last = lines[maxLines - 1] ?? '';
      while (last && measureText(`${last}...`, font, size, 0) > maxWidth) {
        last = last.slice(0, -1).trimEnd();
      }
      lines[maxLines - 1] = `${last}...`;
    }
  }
  return lines;
}

type FooterField = { label: string; value: string };

function drawFooter(page: PdfPage, fields: FooterField[], baseY: number): void {
  if (fields.length === 0) return;

  const columnWidth = CONTENT_WIDTH / fields.length;
  const firstCenter = CENTER_X - CONTENT_WIDTH / 2 + columnWidth / 2;

  fields.forEach((field, index) => {
    const centerX = firstCenter + index * columnWidth;
    page.centeredText(field.label.toUpperCase(), centerX, baseY + 18, {
      size: 8,
      color: MUTED,
      charSpacing: 1.1,
    });
    const valueSize = fitSize(
      toLatin1(field.value),
      'Helvetica-Bold',
      12,
      8,
      columnWidth - 24,
    );
    page.centeredText(field.value, centerX, baseY, {
      font: 'Helvetica-Bold',
      size: valueSize,
      color: INK,
    });

    if (index > 0) {
      page.vline(centerX - columnWidth / 2, baseY - 4, 32, RULE);
    }
  });
}

/** Compose the certificate page. */
function buildCertificatePage(data: CertificateData): PdfPage {
  const labels = { ...DEFAULT_CERTIFICATE_LABELS, ...data.labels };
  const page = new PdfPage();

  // Paper + double border.
  page.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, PAPER);
  page.strokeRect(
    OUTER_MARGIN,
    OUTER_MARGIN,
    PAGE_WIDTH - OUTER_MARGIN * 2,
    PAGE_HEIGHT - OUTER_MARGIN * 2,
    INK,
    1.2,
  );
  page.strokeRect(
    INNER_MARGIN,
    INNER_MARGIN,
    PAGE_WIDTH - INNER_MARGIN * 2,
    PAGE_HEIGHT - INNER_MARGIN * 2,
    GOLD,
    0.7,
  );

  // Lion mark.
  const markHeight = 56;
  const markWidth = PdfPage.markWidth(LION_MARK, markHeight);
  page.vectorMark(LION_MARK, CENTER_X - markWidth / 2, 466, markHeight, INK);

  // Heading.
  page.centeredText(labels.heading.toUpperCase(), CENTER_X, 432, {
    font: 'Helvetica-Bold',
    size: 15,
    color: INK,
    charSpacing: 5,
  });
  page.hairline(CENTER_X, 416, 80, GOLD, 2);

  /*
   * The citation block (recipient + course) is centred in the band between the
   * gold rule and the footer divider rather than pinned to fixed baselines.
   * Course titles run from "Sanctuary 2" to a wrapped two-liner and names shrink
   * to fit, so a fixed layout either strands whitespace above the divider on the
   * short ones or crowds it on the long ones. Measuring the block first and
   * centring it keeps both balanced.
   */
  const name = toLatin1(data.recipientName).trim();
  const nameSize = fitSize(name, 'Helvetica-Bold', 36, 18, CONTENT_WIDTH);
  const title = toLatin1(data.courseTitle).trim();
  const titleSize = fitSize(title, 'Helvetica-Bold', 21, 14, CONTENT_WIDTH);
  const titleLines = wrapLines(title, 'Helvetica-Bold', titleSize, CONTENT_WIDTH, 2);
  const titleLeading = titleSize + 7;

  // Gaps between successive baselines, top-down through the block.
  const presentedToToName = nameSize * 1.06;
  const nameToRule = 16;
  const ruleToCompletionLine = 30;
  const completionLineToTitle = 30;

  const blockHeight =
    11 + // presentedTo cap height
    presentedToToName +
    nameToRule +
    ruleToCompletionLine +
    completionLineToTitle +
    (titleLines.length - 1) * titleLeading +
    8; // descender room under the last title line

  const blockTop = (BAND_BOTTOM + BAND_TOP) / 2 + blockHeight / 2;

  const presentedToY = blockTop - 11;
  page.centeredText(labels.presentedTo, CENTER_X, presentedToY, {
    size: 11,
    color: MUTED,
    charSpacing: 0.4,
  });

  const nameY = presentedToY - presentedToToName;
  page.centeredText(name, CENTER_X, nameY, {
    font: 'Helvetica-Bold',
    size: nameSize,
    color: INK,
  });

  const nameWidth = measureText(name, 'Helvetica-Bold', nameSize);
  const nameRuleY = nameY - nameToRule;
  page.hairline(CENTER_X, nameRuleY, Math.min(nameWidth + 72, CONTENT_WIDTH), RULE);

  const completionLineY = nameRuleY - ruleToCompletionLine;
  page.centeredText(labels.completionLine, CENTER_X, completionLineY, {
    size: 11,
    color: MUTED,
    charSpacing: 0.4,
  });

  const titleTopY = completionLineY - completionLineToTitle;
  titleLines.forEach((line, index) => {
    page.centeredText(line, CENTER_X, titleTopY - index * titleLeading, {
      font: 'Helvetica-Bold',
      size: titleSize,
      color: INK_SOFT,
    });
  });

  // Footer facts — only the ones this enrollment actually has.
  const completedOn = data.completedAt
    ? formatCompletionDate(data.completedAt, data.locale)
    : null;
  const fields: FooterField[] = [];
  if (completedOn) fields.push({ label: labels.completedOn, value: completedOn });
  if (data.certificateNumber) {
    fields.push({ label: labels.certificateNumber, value: data.certificateNumber });
  }

  page.hairline(CENTER_X, 196, CONTENT_WIDTH, RULE, 0.6);
  drawFooter(page, fields, 150);

  // Wordmark.
  const wordmarkHeight = 13;
  const wordmarkWidth = PdfPage.markWidth(LION_WORDMARK, wordmarkHeight);
  page.vectorMark(LION_WORDMARK, CENTER_X - wordmarkWidth / 2, 84, wordmarkHeight, INK);
  page.centeredText(labels.issuedBy, CENTER_X, 64, {
    size: 8,
    color: MUTED,
    charSpacing: 0.8,
  });

  return page;
}

/** Build the certificate as raw PDF bytes. */
export function buildCertificatePdf(data: CertificateData): Uint8Array<ArrayBuffer> {
  const labels = { ...DEFAULT_CERTIFICATE_LABELS, ...data.labels };
  return renderPdf(buildCertificatePage(data), {
    title: `${labels.heading} - ${data.courseTitle}`,
    author: 'Lion Energy',
    subject: data.recipientName,
  });
}

/** Build the certificate as a blob ready for download or preview. */
export function buildCertificateBlob(data: CertificateData): Blob {
  return new Blob([buildCertificatePdf(data)], { type: 'application/pdf' });
}

/** Filesystem-safe slug for the download filename. */
function slugify(text: string): string {
  return (
    toLatin1(text)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'certificate'
  );
}

export function certificateFilename(data: CertificateData): string {
  return `lion-energy-certificate-${slugify(data.courseTitle)}.pdf`;
}

/** Build the certificate and hand it to the browser as a download. */
export function downloadCertificatePdf(data: CertificateData): void {
  const url = URL.createObjectURL(buildCertificateBlob(data));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = certificateFilename(data);
  anchor.click();
  // Revoked on the next tick, not inline: Safari can still be reading the blob
  // when `click()` returns, and a revoked URL there yields an empty download.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
